import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0506 from "../lesson-06/lesson";

const linkLesson0506 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0506.id);

const theory = ` <h3 class=sectionHead>What you will learn</h3>
<p>Shaping is an essential tool for every architect. Whether the shape of an architectural element (building, room, wall) is changed for artistic, practical or social purposes, it always bears a certain meaning. Shapes can be carriers of symbols, they can be designed to serve a specific purpose, they sometimes offend people or support them, etc. The influence of the shape of elements used in architectural design cannot be denied, and the same is true for acoustics. The question is if there is a way to reconcile aesthetic and acoustic design criteria the shape of an element needs to meet. To address this issue, we need to understand how different factors regarding the shape of a room or its elements influence the acoustics of that room. Only with the full understanding of these principles can a completely integrated design be created.
<h3 class=sectionHead>Volume</h3>
<p>Depending on the sheer size of a room, in some instances it is more important to use the enclosed volume as a design parameter, while in others it is more appropriate to address the proportions of a room. To illustrate the difference, let us consider an example of two rooms with different dimensions, but the same proportions. The same activity is performed in both rooms, e.g. a lecture with a teacher speaking. The size and shape of both rooms is illustrated in <a href="#" data-img-src="${config.ASSETS_URL}/05/04/theory/figure-1.svg" data-img-alt="Figure 1. The relative size of the two rooms. The dotted line represents the outline of the smaller room.">Figure 1</a>.
<p>
   The small room has the dimensions (L x W x H) of 9 m x 9 m x 4.5 m, and the volume of 364.5
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
          <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   .<br class=newline>The large room has the dimensions of 18 m x 18 m x 9 m, and the volume of 2916
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
        <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   .
<p>
   The volume of the large room is eight times the volume of the small room. The overall surface area of the large room is four times the overall surface area of the small room. If all interior surface materials are the same in both rooms, and the absorption of the audience is ignored, the reverberation in the large room is two times longer than the one in the small room.<br class=newline>From the acoustical point of view, the difference between these two rooms is considerable. In the small room, reverberation is shorter, but the speech is louder compared to the large room, assuming the speaker delivers their speech identically in both rooms. Given the difference in the physical parameters of both rooms, the speech level is ultimately 6 dB lower in the large room.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/04/theory/figure-1.svg" alt="Figure 1. The relative size of the two rooms. The dotted line represents the outline of the smaller room."><figcaption class=caption>Figure 1. The relative size of the two rooms. The dotted line represents the outline of the smaller room.</figcaption></figure>
<p>Note that longer reverberation in the large room stems from longer trajectories the individual reflections need to travel. As a consequence, they arrive later in time, and the difference in arrival times of individual sound reflections is larger, making the decay slope less steep. So, longer reverberation does not come from more reflections in the room, but from the fact that the entire reflectogram is ”stretched” in time, compared to the one in the small room.
<p>
   The difference between the reflectograms recorded in these two rooms, and the aforementioned ”stretch” in time is clearly visible on the comparison chart shown in <a href="#" data-img-alt="Figure 2. The comparison of the reflectograms recorded in the small and the large room." data-img-src=${config.ASSETS_URL}/05/04/theory/figure-2.svg>Figure 2</a>. The chart shows the first 20 sound reflections that arrive to the listener in the large room (blue dots) after the onset of direct sound at time = 0, and the same for the small room (orange dots).<br class=newline>In the small room, all 20 reflections arrive within the time interval from 10 ms to 63 ms from the onset of the sound source. In the large room, the first reflection arrives after also after 10 ms, but the second one comes after 40 ms and the twentieth only after 115 ms. The first reflection is in fact the reflection with the floor.<br class=newline>Besides the significant difference in arrival times of individual reflections observed in these two rooms, a clear difference in their amplitude (level) is visible as well, confirming that SPL is indeed higher in the small room.
   <figure class=figure>
<p><img class=graphics alt="Figure 2. The comparison of the reflectograms recorded in the small and the large room." src=${config.ASSETS_URL}/05/04/theory/figure-2.svg><figcaption class=caption>Figure 2. The comparison of the reflectograms recorded in the small and the large room.</figcaption></figure>
<p>
   The level difference between the reflections in the two rooms becomes even clearer if time information is left out, and the SPL of the first 25 reflections are compared in pairs, as shown in <a href="#" data-img-alt="Figure 3. The paired comparison of the SPL of individual reflections in the two rooms. The reflections are ordered as they arrive at the listener position." data-img-src=${config.ASSETS_URL}/05/04/theory/figure-3.svg>Figure 3</a>.
   <figure class=figure>
<p><img class=graphics alt="Figure 3. The paired comparison of the SPL of individual reflections in the two rooms. The reflections are ordered as they arrive at the listener position." src=${config.ASSETS_URL}/05/04/theory/figure-3.svg><figcaption class=caption>Figure 3. The paired comparison of the SPL of individual reflections in the two rooms. The reflections are ordered as they arrive at the listener position.</figcaption></figure>
<p>In the presented example, the two rooms have different volumes, but the source-receiver distance is the same in both of them. However, due to its greater size, in the large room the listener/receiver can move further away from the source than they could in the small room. The changes in the sound that happen with changing distance between the source and the listener/receiver is explained in detail in <a href="${linkLesson0506}" target="_blank">Lesson 5.6</a>.
<h3 class=sectionHead>Standing waves and room proportions in small rectangular rooms</h3>
<h4 class=subsectionHead>Standing waves</h4>
<p>
   We already observed that waves interact with a room mainly by reflecting off its boundaries. We also know that waves have different wavelengths depending on the frequency. The human hearing system of a young adult can perceive sound within the frequency range from 20 Hz to 20 000 Hz. If we calculate the corresponding wavelengths
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
   </math>
   of these boundary frequencies at 20<span class=tcrm-1000>°</span>C (the usual room temperature), we get:
   <math display=block xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>></mo>
      <mi>λ</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>343</mn>
         </mrow>
         <mrow>
            <mn>20</mn>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>></mo>
      <mn>17</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>15</mn>
   </math>
   <math display=block xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>></mo>
      <mi>λ</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>343</mn>
         </mrow>
         <mrow>
            <mn>20000</mn>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>></mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>01715</mn>
   </math>
<p>
   with<br class=newline>c in m/s<br class=newline>f in Hz<br class=newline>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
   </math>
   in m
<p>
   In other words, the sound waves in the audible frequency range have wavelengths between 17 mm and 17 m. A comparison between the smallest and the largest audible wavelength is shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/04/theory/figure-4.svg" data-img-alt="Figure 4. Size comparison of the smallest and the largest audible wavelength.">Figure 4</a>.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/04/theory/figure-4.svg" alt="Figure 4. Size comparison of the smallest and the largest audible wavelength."><figcaption class=caption>Figure 4. Size comparison of the smallest and the largest audible wavelength.</figcaption></figure>
<p>A shoe-box-shaped room as a closed cavity is prone to the formation of standing waves at its natural resonance frequencies. It is known that a standing wave is a stationary wave which oscillates in time, but is stable in space. If a person walks around the room in which a standing wave has been formed, they will find that there are regions where the sound is very loud, but there are also regions where the sound is (almost) inaudible. These regions do not change their positions. The regions where the sound is inaudible or very quiet, i.e. where the amplitude of the standing wave is very small, are called the “nodes” of the standing wave. On the other hand, the regions where the sound is the loudest, i.e. where the amplitude of the standing wave reaches its maximum, are called “antinodes”.
<p><a href="#" data-img-src="${config.ASSETS_URL}/05/04/theory/figure-5.svg" data-img-alt="Figure 5. An example of axial room modes formed along the length (left) and along the height (right) of the room. The sharp minima in both standing waves correspond to the pressure minima or nodes of these standing waves.">Figure 5</a> shows the formation of axial standing waves along the length and along the height of the room. Taking into account the dimensions of the room (L x W x H = 18 m x 18 m x 9 m), the displayed standing wave along the length of the room is formed at the frequency of 28.5 Hz. The one formed along the height of the room occurs at 57 Hz. Note that standing waves do not form only along a single dimension of the room (axial modes), as shown here. Instead, they can form along two dimensions of a room (tangential modes) or all three dimensions of a room (oblique modes).<br class=newline>The position of the sound source in the room is of great importance. If the source is located in the node of a particular mode, it is not able to excite that particular mode. On the other hand, if the source is put in the antinode of a mode, that mode is excited very strongly. As all modes have antinodes in the corners of a room, a source put there strongly excites all the modes, thus creating a response of the room that is very rich in quantity at low frequencies. At the same time, its quality is very poor, as the low-frequency distribution of sound throughout the room is extremely uneven. This unevenness remains even if the source is put somewhere else in the room, as some modes are strongly excited, and some are not excited at all.<br class=newline>The position of the listener in the room is equally important, when it comes to standing waves that are formed inside. The response of the room at any listener position is uneven due to the fact that any given position might be an antinode for certain modes, a node for some others, or something in between for all the rest. Quality-wise, the worst listener position is in the corner of a room, as all modes have antinodes there, and this results in an extremely uneven response of the room. Again, the unevenness still remains at any other listener position, but it is not as extreme as it is in the corner.<br class=newline>
<p>The lowest (fundamental) modes in the room bring the greatest unevenness in the response of the room, as they stand alone and far away from neighbouring modes, if observed on the frequency axis. As the frequency increases, the number of modes increases as well, and they get closer together in frequency. Eventually, the so-called modal density becomes high enough and the modes cannot be perceived individually anymore.
<p>
   The resonance frequencies of rooms and the resulting modes depend solely on room dimensions. The larger the room, the lower the frequencies of the fundamental modes. Unfortunately, in common-sized rooms the fundamental modes appear in the low part of the audible frequency range, i.e. usually between 25 and 50 Hz. This means that the reproduction of low-frequency sound in such rooms is greatly affected by the appearance of room modes.<br class=newline>In rooms designed and used as recording studios, broadcasting studios, control rooms, or listening rooms, the proportions of room dimensions, the position of the monitoring speakers, and the position of the listener are carefully chosen to reduce the described colouration of sound. Such rooms are acoustically treated to minimize this effect. The unevenness of the low-frequency response of the entire system composed of the loudspeakers and the room is sometimes addressed by means of electronic optimization for the given listener position, through which the audio signal itself is modified to compensate for the unevenness and the imperfections of the response of the system.<br class=newline>In normal rooms that are not specifically designed for music, room modes are not so important, and their influence can be neglected. The frequency range important for speech (500-4000 Hz) is generally not affected by room modes.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/04/theory/figure-5.svg" alt="Figure 5. An example of axial room modes formed along the length (left) and along the height (right) of the room. The sharp minima in both standing waves correspond to the pressure minima or nodes of these standing waves."><figcaption class=caption>Figure 5. An example of axial room modes formed along the length (left) and along the height (right) of the room. The sharp minima in both standing waves correspond to the pressure minima or nodes of these standing waves.</figcaption></figure>
<p>In any rectangular room, the frequencies of its modes can be calculated using the following equation:
<table class=equation>
   <tr>
      <td style="font-size: 14px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi>p</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>q</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>r</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mstyle mathsize=1.61em>
                     <mrow>
                        <mo form=prefix fence=true>(</mo>
                     </mrow>
                  </mstyle>
                  <mfrac>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                  </mfrac>
                  <msup>
                     <mrow>
                        <mstyle mathsize=1.61em>
                           <mrow>
                              <mo form=prefix fence=true>)</mo>
                           </mrow>
                        </mstyle>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <mstyle mathsize=1.61em>
                     <mrow>
                        <mo form=prefix fence=true>(</mo>
                     </mrow>
                  </mstyle>
                  <mfrac>
                     <mrow>
                        <mi>q</mi>
                     </mrow>
                     <mrow>
                        <mi>W</mi>
                     </mrow>
                  </mfrac>
                  <msup>
                     <mrow>
                        <mstyle mathsize=1.61em>
                           <mrow>
                              <mo form=prefix fence=true>)</mo>
                           </mrow>
                        </mstyle>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <mstyle mathsize=1.61em>
                     <mrow>
                        <mo form=prefix fence=true>(</mo>
                     </mrow>
                  </mstyle>
                  <mfrac>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                  </mfrac>
                  <msup>
                     <mrow>
                        <mstyle mathsize=1.61em>
                           <mrow>
                              <mo form=prefix fence=true>)</mo>
                           </mrow>
                        </mstyle>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   In the equation above,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>c</mi>
   </math>
   is the speed of sound in air,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>q</mi>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   are non-negative integer numbers, i.e. 0, 1, 2,..., while
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>W</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>H</mi>
   </math>
   are the length, width and height of the room, respectively.<br class=newline>
<p>
   The modes themselves are marked as (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>q</mi>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   ), and their frequencies as
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mi>p</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>q</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </msub>
   </math>
   .<br class=newline>
<p>Note that at least one of these three numbers must be different from zero. If all three were equal to zero, the resulting mode frequency would also be equal to zero, which has no practical meaning.<br class=newline>
<p>If exactly one of these numbers is different from zero, and the other two are equal to zero, we are talking about axial modes formed along one dimension of the room, as illustrated in <a href="#" data-img-src="${config.ASSETS_URL}/05/04/theory/figure-5.svg" data-img-alt="Figure 5. An example of axial room modes formed along the length (left) and along the height (right) of the room. The sharp minima in both standing waves correspond to the pressure minima or nodes of these standing waves.">Figure 5</a>, where q and r are 3.<br class=newline>
<p>
   For example, if
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
   </math>
   = 1,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>q</mi>
   </math>
   = 0, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   = 0, we get the axial mode (1,0,0), which is in fact the lowest axial mode that is formed along the length of the room. In this case, Eq. 1 is reduced to:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-punc>,</mo>
                  <mn>0</mn>
                  <mo class=MathClass-punc>,</mo>
                  <mn>0</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>L</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   As shown with Eq. 2, the frequency of this axial mode can be calculated knowing only the length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
   </math>
   of the room, while the other two dimensions are not important.
<p>If exactly two of these numbers are different from zero, and the third one is equal to zero, we are talking about tangential modes formed along two dimensions of the room.<br class=newline>
<p>Complex room shapes are not necessarily free of resonance effects (room modes), but their mode frequencies cannot be determined analytically, i.e. using a simple formula. Instead, a simulation might reveal possible room modes. Finally, it should be noted that in any space the amplitude of room modes depend on the wall materials.
<h4 class=subsectionHead>Criteria for finding favourable room proportions</h4>
<h5>The basic criteria based on IEC, ITU and EBU recommendations</h5>
<p>The basic criteria used for evaluating the influence of room proportions on the frequency distribution of room modes in a rectangular room are given in the following recommendations: IEC TR 60268-13, ITU-R BS 1116-1, and EBU 3276, as follows:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <mi>W</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≤</mo>
            <mfrac>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≤</mo>
            <mn>4</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>5</mn>
            <mo class=MathClass-bin>⋅</mo>
            <mfrac>
               <mrow>
                  <mi>W</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <mn>4</mn>
         </math>
      <td class=eq-no>(3)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel><</mo>
            <mn>3</mn>
         </math>
      <td class=eq-no>(4)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <mi>W</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel><</mo>
            <mn>3</mn>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   In the equations above,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
   </math>
   is the length of the room (its largest dimension),
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>W</mi>
   </math>
   is the width, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>H</mi>
   </math>
   is the height.
<p>The listed basic recommendations come from the IEC document and define the so-called Sabine-type rooms, in which the three dimensions of the room are similar in size (both the length-to-height ratio and the width-to-height ratio need to be kept below 3). According to these recommendations, rooms with one of the dimensions considerably larger or smaller than the other two, such as long hallways or large rooms with low ceilings, respectively, are considered to have unfavourable proportions.<br class=newline>The basic IEC recommendations given above allow the length-to-width ratio to be equal to 1, i.e. the floor plan of the room can be a perfect square. Additionally, they also allow both the length-to-height ratio and width-to height-ratio to be equal to 2:1. As all this can lead to mode grouping, i.e. it would be possible to have two or even more modes with the same mode frequency, additional requirements were proposed in ITU and EBU recommendations in order to avoid this issue, as follows:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≥</mo>
            <mn>1</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>1</mn>
         </math>
      <td class=eq-no>(6)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≤</mo>
            <mn>1</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>9</mn>
         </math>
      <td class=eq-no>(7)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≥</mo>
            <mn>2</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>1</mn>
         </math>
      <td class=eq-no>(8)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <mi>W</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≤</mo>
            <mn>1</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>9</mn>
         </math>
      <td class=eq-no>(9)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mfrac>
               <mrow>
                  <mi>W</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≤</mo>
            <mn>2</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>1</mn>
         </math>
      <td class=eq-no>(10)
</table>
<p>The additional criteria stipulate that the ratio of any two room dimensions can never be equal or very close to 1 or 2. In other words, if a room is to have favourable proportions, no room dimension should be equal or almost equal to another, nor should it be (almost) exactly twice as long or half as long as any other.
<h5 class=subsubsectionHead>The Bolt area</h5>
<p>
   One way of assessing whether the proportions of a room are favourable or unfavourable implies the use of the so-called Bolt area. As shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/04/theory/figure-6.svg" data-img-alt="Figure 6. The Bolt area as an indicator of (un)favourable room proportions.">Figure 6</a>, it is the closed area on the chart which displays the length of the room vs. its width, both divided by the height of the room. According to Richard H. Bolt [1], a room has favourable proportions if the point defined as (W/H, L/H) falls inside the area indicated on the chart.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/04/theory/figure-6.svg" alt="Figure 6. The Bolt area as an indicator of (un)favourable room proportions."><figcaption class=caption>Figure 6. The Bolt area as an indicator of (un)favourable room proportions.</figcaption></figure>
<p>For example, the points B, C, D, F and H on the chart fall within the Bolt area, so the proportions of the corresponding rooms are favourable. The room that yielded point G has borderline favorable proportions, and the rooms that yielded points A and E have unfavourable proportions.
<h5 class=subsubsectionHead>The Bonello criterion</h5>
<p>The Bonello criterion is another way of assessing whether a room has favourable or unfavourable proportions. In essence, this criterion states that the number of room modes in standard 1/3-octave bands must grow (or at least remain constant) as the center frequency of these bands increases. To assess whether this is true or not for a given room, the audible low-frequency range is divided into standard 1/3-octave bands with standard center frequencies (16 Hz, 20 Hz, 25 Hz, 31.5 Hz, 40 Hz, 50 Hz, etc.). Then, room mode frequencies are calculated from Eq. 1 by inputting the actual length, width and height of the room. The last step is to simply count the number of modes that fall within each 1/3-octave band.<br class=newline>
<p>
   An example of the Bonello criterion applied to two different rooms is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/04/theory/figure-7.svg data-img-alt="Figure 7. The Bonello criterion as an indicator of (un)favourable room proportions applied to a room with favourable (blue) and a room with unfavourable (red) proportions.">Figure 7</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/04/theory/figure-7.svg alt="Figure 7. The Bonello criterion as an indicator of (un)favourable room proportions applied to a room with favourable (blue) and a room with unfavourable (red) proportions."><figcaption class=caption>Figure 7. The Bonello criterion as an indicator of (un)favourable room proportions applied to a room with favourable (blue) and a room with unfavourable (red) proportions.</figcaption></figure>
<h5 class=subsubsectionHead>An example of the assessment of room proportions</h5>
<p>An example of assessing whether a room has (un)favourable room proportions is shown on two example rooms, both of which can still be considered small from the viewpoint of room acoustics, i.e. they are small enough to have distinct and prominent modes in the audible low-frequency range. The two rooms have considerably different dimensions and room proportions.<br class=newline>As <a href="#" data-img-src=${config.ASSETS_URL}/05/04/theory/figure-7.svg data-img-alt="Figure 7. The Bonello criterion as an indicator of (un)favourable room proportions applied to a room with favourable (blue) and a room with unfavourable (red) proportions.">Figure 7</a>, in fact, illustrates the Bonello criterion applied to these particular rooms, let us name the two rooms as ”red” and ”blue” to match the colours used in the corresponding chart.<br class=newline>
<p>
   The ”red” room in this example has
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
   </math>
   = 9.5 m,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>W</mi>
   </math>
   = 4.6 m, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>H</mi>
   </math>
   = 2.9 m, yielding
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
   </math>
   :
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>W</mi>
   </math>
   :
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>H</mi>
   </math>
   = 3.28 : 1.59 : 1, with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>W</mi>
   </math>
   = 2.07.<br class=newline>The ”blue” room in this example has
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
   </math>
   = 6.0 m,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>W</mi>
   </math>
   = 3.9 m, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>H</mi>
   </math>
   = 2.7 m, yielding
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
   </math>
   :
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>W</mi>
   </math>
   :
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>H</mi>
   </math>
   = 2.22 : 1.44 : 1, with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>L</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>W</mi>
   </math>
   = 1.54.<br class=newline>
<p>If we take the relevant dimension ratios of both rooms and test them against the IEC/ITU/EBU criteria and the Bolt area criterion, we find that the ”blue” room satisfies all these criteria. On the other hand, the ”red” room does not satisfy any of the described criteria for favourable room proportions due to its excessive length compared to the height, and to the fact that the room is almost exactly twice as long than it is wide.<br class=newline>The assessment conducted according to the Bonello criterion, as shown on the chart in <a href="#" data-img-src=${config.ASSETS_URL}/05/04/theory/figure-7.svg data-img-alt="Figure 7. The Bonello criterion as an indicator of (un)favourable room proportions applied to a room with favourable (blue) and a room with unfavourable (red) proportions.">Figure 7</a>, reveals that the ”blue” room has favourable room proportions, as the number of modes indeed increases (or at least remains constant) as the center frequency of 1/3-octave bands increases. On the other hand, the ”red” room has unfavourable proportions, as there is a single mode in the 20 Hz band, then there are none in the next two bands, then there are two modes in the 40 Hz band, but only one mode in the 50 Hz band. At this point, the number of modes finally starts to increase monotonically with frequency.<br class=newline>The size difference between these two rooms is reflected in the chart as well. The lowest mode in the ”red” room is the axial mode along its length, and its frequency is equal to 18.1 Hz, which puts it within the 1/3-octave band at 20 Hz. As the ”blue room” is smaller, its lowest mode is found at a considerably higher frequency. In fact, it is the axial mode formed along the length of the room at the frequency of 28.6 Hz, i.e. within the 1/3 octave band at 31.5 Hz.<br class=newline>
<h3 class=sectionHead>Shape of the room</h3>
<h4 class=subsectionHead>General shape of the room</h4>
<p>As mentioned before, in a large enough room, room proportions are less important than its absolute size. However, the general shape of the room has a profound influence on the behaviour of sound inside such a space. In room acoustics, the rooms one has to deal with are usually shoe-box-shaped. These are rooms where the Sabine formula is applicable. Some rooms are proportionally different, sometimes the floor area is considerable, whereas the height is relatively small, e.g. in a large classroom or an open-plan office or an industrial hall, etc. The behaviour of sound in conventionally and unconventionally shaped spaces is illustrated in the principle part of this lesson.
<h4 class=subsectionHead>Ceiling height</h4>
<p>Ceiling height is an important factor in architectural acoustics. The ceiling height is determined to yield the overall volume of the room/hall appropriate for the type of program that takes place inside, and for the designed audience size (number of seats). Earlier in this lesson we have seen that larger volume leads to lower sound pressure level and longer reverberation time. As the determining factor for the room volume (for a fixed floor area), the ceiling height can be put into the same relationship with sound pressure level and reverberation time. Keep in mind that increasing the ceiling height also increases the surface area of the walls. The designer/acoustician can use this extra surface area to add more absorption into the room, if needed.
<p>As the special and critical case of acoustic design, concert halls benefit from strong early lateral reflections (reflections coming from the side), as they are associated with a positive musical experience. The volume added by raising the ceiling can also create pleasant reverberation to support live performance. In the context of music performance, a certain amount of reverberation is always welcomed by musicians and audience. The optimum amount depends primarily on the type of music that is performed. In spaces built for speech-based events, the reverberation is usually kept short for a clearer sound and, consequently, better speech intelligibility. The absorption is strategically placed in the room to control the reverberation while facilitating strong early reflections that aid the direct sound.
<h3 class=sectionHead>The shape of individual surfaces in the room</h3>
<p>Along with size and building material, the shape of individual room surfaces determines how these surfaces interact with the incoming sound waves.
<h4 class=subsectionHead>Flat surfaces</h4>
<p>Large, almost flat surfaces (with dimensions larger than the wavelength of sound) tend to reflect sound specularly, i.e. the angle of incidence is equal to the angle of reflection. The stiffer and smoother the surface material (plaster, concrete, brick, wood, glass), the more sound it reflects.<br class=newline>
<p>The size of a flat surface directly determines the frequency range within which the surface behaves as a reflector, as illustrated in <a href="#" data-img-src="${config.ASSETS_URL}/05/04/theory/figure-8.svg" data-img-alt="Figure 8. How the relationship between the wavelength (frequency) of the sound wave and the size of the object determines their interaction.">Figure 8</a>. The terms ”low” and ”high” frequencies used in the illustration do not define a fixed and absolute frequency range, but are determined from the actual size of the object/surface the sound wave interacts with. If the wavelength of sound is significantly larger than the object, the sound wave will simply pass “through” (around) the object. Any frequency at which this happens is considered low frequency for that particular object. When the wavelength is similar to the size of the object, we are talking about the mid-frequency range for that object, in which the object tends to scatter such sound waves more or less omnidirectionally. Finally, when the wavelength of sound is much smaller than the object, the object behaves as a specular reflector.
<p>
   Sounds in everyday life are broadband, i.e. their spectral content consists of numerous components with different frequencies. Some of that sound at low frequencies passes around the reflector, some sound at middle frequencies is scattered in every direction, and the rest of the sound at high frequencies is reflected specularly. One could say that the sound is filtered geometrically in this manner. This is why the way a sound source sounds in the room changes with different sizes of reflective room surfaces, but also the surfaces of objects in the room such as furniture.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/04/theory/figure-8.svg" alt="Figure 8. How the relationship between the wavelength (frequency) of the sound wave and the size of the object determines their interaction."><figcaption class=caption>Figure 8. How the relationship between the wavelength (frequency) of the sound wave and the size of the object determines their interaction.</figcaption></figure>
<h4 class=subsectionHead>Corrugated surfaces</h4>
<p>A corrugated surface made of a hard, stiff material scatters an incoming sound wave. The sound wave is broken up into many different waves depending on the pattern and the size of the corrugation. The size of the corrugation determines the frequency range in which this process is still effective. The lower the frequency of the sound wave that needs to be affected, the larger the corrugation needs to be. The affected frequency range is determined only from the characteristic depth of corrugation, regardless of the shape of the corrugation. The characteristic depth can range from a few centimeters (effective only at the highest audible frequencies) to a few meters (for the lowest part of the audible frequency range).
<h4 class=subsectionHead>Convex and concave surfaces</h4>
<p>
   As illustrated in <a href="#" data-img-src="${config.ASSETS_URL}/05/04/theory/figure-9.svg" data-img-alt="Figure 9. Interaction of sound waves modelled as rays with convex and concave surfaces.">Figure 9</a>, convex surfaces made of hard, reflective materials always spread out the energy of an incoming sound wave, thus facilitating the attenuation of the sound energy density. On the other hand, concave surfaces exhibit more complex behaviour. Concave surfaces have focal points or lines associated with their shape, which can be spherical, ellipsoidal, cylindrical, or some other. As a result, they always cause convergence of sound and the amplification of the energy density of reflected sound in the area of space between the surface and its focus. Beyond the focal point (line), the sound is, in fact spread, and its energy is attenuated. The location of the acoustic focus, however, does not depend only on the shape of the surface, but also on the position of the sound source relative to the surface. In other words, acoustic focal points are in different locations than the geometrical focal points associated only with the shape, and they change as the location of the source changes.<br class=newline>Concave geometries frequently cause problems in room acoustics as they tend to focus sound onto small zones around their focal points, in which a high concentration - or amplification - of sound energy is found.<br class=newline>Focusing in itself is not necessarily disadvantageous, as a stronger and clearer sound may be perceived in the affected zones. However, it generally leads to an extremely uneven distribution of sound in the room and is, therefore, avoided as much as possible.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/04/theory/figure-9.svg" alt="Figure 9. Interaction of sound waves modelled as rays with convex and concave surfaces."><figcaption class=caption>Figure 9. Interaction of sound waves modelled as rays with convex and concave surfaces.</figcaption></figure>
<h3 class=sectionHead>The positioning of absorption in the room</h3>
<p>To dampen a room acoustically, thus making the acoustical conditions appropriate for a given purpose, one needs to put a certain amount of acoustically absorptive material into that room. The amount of absorption in a room, however, is not the only influential factor. How the absorptive material is distributed on the room surfaces is just as important. For example, it can be distributed evenly on all surfaces, but this impractical as the floor is usually hard en smooth (both for circulation and hygiene purposes). In a room with a high ceiling, it can be put mostly at listening height, but also on the ceiling, high above the audience. There are many other ways to place the absorptive material on the surfaces of the room. In general, the positioning of the absorption in a room has more impact on the amount of reverberation and the decay rate, while the amount of absorption has an impact on the sound pressure level in the diffuse field.<br class=newline>
<p>
   <a href="#" data-img-src=${config.ASSETS_URL}/05/04/theory/figure-10.svg data-img-alt="Figure 10. The decay of sound at one position in a room with three different amounts of sound absorption.">Figure 10</a> illustrates the decay of sound over time in a room with dimensions 15 m x 10 m x 4 m (L x W x H) for three different cases that illustrate the influence of the amount of absorption and its positioning in the room on the rate of decay.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/04/theory/figure-10.svg alt="Figure 10. The decay of sound at one position in a room with three different amounts of sound absorption."><figcaption class=caption>Figure 10. The decay of sound at one position in a room with three different amounts of sound absorption.</figcaption></figure>
<p>
   The amount of absorption on a given surface is expressed in square meters, and is calculated by multiplying the absorption coefficient of the material with the surface area of the corresponding surface. By summing the contribution of all room surfaces, we find the so-called total absorption area
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>A</mi>
   </math>
   , expressed in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
          <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   .<br class=newline>
<p>The three investigated cases are defined as follows:
<ol class=enumerate1>
   <li class=enumerate id=x1-17004x1>
      The 5% allround case - absorptive material with the absorption coefficient of 5% is put on all the surfaces of the room. The total absorption area is equal to 25
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </math>
      .
   <li class=enumerate id=x1-17006x2>
      The 30% allround case - absorptive material with the absorption coefficient of 30% is put on all the surfaces of the room. The total absorption area is equal to 150
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </math>
      .
   <li class=enumerate id=x1-17008x3>
      The 5-70% case - absorptive material with the absorption coefficient of 70% is put on the ceiling. All the other surfaces of the room are covered with absorptive material with the absorption coefficient of 5%. The total absorption area is equal to 122.5
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </math>
      . This is the most realistic case in this list. In reality, rooms do not absorb sound equally on all surfaces.
</ol>
<p>As far as the amount of absorption introduced into the room is concerned, case 1 significantly differs from cases 2 and 3. In particular, the amount of absorption in case 1 is 5 to 6 times smaller than in cases 2 and 3. Consequently, level of the reverberation in case 1 is higher. Even though the absorption area of the the two other cases is rather similar, the level of the reverberation and decay rate are quite different.<br class=newline>
<p>The influence of the positioning of absorption on reverberation is presented with cases 2 and 3. The amount of absorption is very similar in both cases. However, in case 2, absorption is distributed evenly on all surfaces, whereas in case 3 it is concentrated on the ceiling and is only marginally present on all other surfaces. The chart shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/04/theory/figure-10.svg data-img-alt="Figure 10. The decay of sound at one position in a room with three different amounts of sound absorption.">Figure 10</a> reveals that these two cases yields quite different sound decay in the investigated room. In fact, by placing most of the absorption on the ceiling, as was done in case 3, the resulting decay rate (slope of the decay curves) of sound (light blue) ultimately becomes very similar to the one in case 1 (orange curve) that was achieved with five times less absorption. The direct comparison of the decay curves in cases 2 and 3 reveals that a given amount of absorption distributed evenly in the room (case 2) results in a much faster decay of sound (dark blue), compared to the uneven distribution of absorption in case 3 and the resulting decay curve (light blue).<br class=newline>
<p>Why does the distribution of the absorption have an influence on the reverberation time? The dimensions of the room (15 m x 10 m x 4 m) gives an indication of the possible answer. The height of the room is much lower than its length and width, as is often the case with sports halls. The possible propagation paths between the walls (between consecutive reflections) are much longer than the ones between the floor and the ceiling. When designing a sports hall, however, the typical practice is to put all or most of the absorption on the ceiling of the hall. As illustrated above, this leads to a lot of absorptive material that is ill-spent by being poorly distributed in the room, i.e. concentrated on the ceiling. Such a solution ultimately reduces the level of the steady-state reverberant sound, but yields a disproportionately long reverberation in relation to the quantity of the absorptive material.
<p>
   Sound propagates at a constant speed in air, if the temperature and humidity of the air are constant. The two opposing surfaces 4m apart have more reflections per unit time than two opposing walls 15m apart in the same time interval. Placing sound absorption in the room in <a href="#" data-img-src=${config.ASSETS_URL}/05/04/theory/figure-10.svg data-img-alt="Figure 10. The decay of sound at one position in a room with three different amounts of sound absorption.">Figure 10</a> on the ceiling and floor therefore decreases the noise levels in the room better than placing absorption on the two walls which are further from each other. In both cases (e.g. placing absorption only on two opposite surfaces) 2D reverberation or even flutter echoes occur. Therefore, in reality it is always recommended to place absorption on the ceiling and partially on walls etc.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/04/theory/figure-11.svg" alt="Figure 11. Reflection path comparison."><figcaption class=caption>Figure 11. Reflection path comparison.</figcaption></figure>
<h3 class=sectionHead>What is shown in the principle part?</h3>
<p>
   The principle part of this lesson strives to examine the behaviour of sound in a fairly regular shoe-box-shaped room in comparison with its behaviour in a bit more unconventionally shaped spaces, namely a long, straight corridor, a u-shaped room and a semi-circular corridor. All these spaces are 4 m high and they are presented in plan view. In practice, different types of surfaces with different absorption characteristics are usually present in a room. All four rooms have roughly the same volume (500-600
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
      <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   ).<br class=newline>Additionally, the difference that can be achieved by changing the position of the absorption material in the room. Therefore, three cases are considered. In the first case, all surfaces are covered with a material that has the absorption coefficient of 5%. The second case is the same, but the absorption coefficient is increased to 30%. The third case employs two different materials: the one with the absorption coefficient of 5% is placed on the walls and floor, while the ceiling is covered with a material with the absorption coefficient of 70%.
<p>A source that emits sound with the sound pressure level of 65 dB is positioned on one end of each room, 1 metre away from the wall to avoid any unwanted boundary effects. Sound Pressure Level (SPL) is plotted for different positions in the room at a height of 1.5 metres above the floor.<br class=newline>
<p>Even though we know that the sound pressure level should eventually drop with the distance from the source, we can see that the corridor ”carries” the sound much further than in the rectangular room. The source-receiver distance in the corridor is longer, but the SPL is as high as the extremity of the rectangular room. The u-shaped space and the semi-circular corridor have the lowest SPL at its extreme, clearly corners or bends have a negative effect on the SPL. Part of the reason is that the corners introduce more directions for the sound to reflect to and that the sound reflects more before arriving around the corner. This is called scattering, which increases the amount of reflections. Additionally, the reflection paths are less direct because the corners introduce a “shadow”, lowering the SPL around the corner even more.
<p>As expected the SPL drops more significantly when the average absorption of all the walls is 30%. In reality, an absorption coefficient of 30% on all surfaces is not realistic. Floor materials rarely absorb much more than 5% unless there is some sort of carpet present. That is why a (quite common) setup was chosen where almost all the absorption is placed on the ceiling. We can see that an absorption coefficient of 70% on the ceiling does not outperform 30% on all surfaces. Spreading absorption materials as much as possible clearly leads to a lower SPL in the room.
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
         <dt>SPL
         <dd>Sound Pressure Level
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-ref1>[1]
   <dd class=thebibliography id=bib-1>
      <p>Bolt, R. H. (1946). Note on normal frequency statistics for rectangular rooms. <span class=cmti-10>The Journal of the Acoustical Society of America</span>, <span class=cmti-10>18</span>(1), 130–133. <a class=url href=https://doi.org/10.1121/1.1916349>https://doi.org/10.1121/1.1916349</a>
   <dt class=thebibliography id=X0-Rychtarikova_2015>[2]
   <dd class=thebibliography id=bib-2>
      <p>Rychtarikova, M., & Chmelik, V. (2015). <span class=cmti-10>Architectural acoustics: Room </span><span class=cmti-10>acoustics </span>(first). Acco.
</dl>`;

export default theory;
